/**
 * Типизированный HTTP-клиент для API ALLSWIPES.
 *
 * Один класс на оба фронтенда. Сессию держит в localStorage, но каждый ответ
 * 401 снимает токен и уведомляет приложение — иначе протухшая сессия даёт
 * бесконечную карусель пустых экранов вместо экрана входа.
 */
import type {
  Admin,
  ChatTurn,
  IntentView,
  IntentDraft,
  IntentDict,
  Intent,
  AdminMe,
  AdminOverview,
  AdminSession,
  ArchiveView,
  AuditEntry,
  BackupFile,
  Brand,
  BrandDraft,
  BrandInvite,
  BrandRole,
  Broadcast,
  Campaign,
  CampaignDraft,
  CampaignLink,
  CampaignView,
  Catalog,
  Claim,
  ClaimStatus,
  CodeBatch,
  CompanyRequest,
  CompanyRequestDraft,
  ConsoleCampaignDetail,
  ConsoleMe,
  Contest,
  ContestEntry,
  ContestStatus,
  ContestView,
  DataDealView,
  DrawReport,
  Entry,
  EntryStatus,
  Feed,
  FeedFilter,
  InvitePreview,
  LinkView,
  ModeratedReview,
  Notification,
  OTPRequest,
  OfferView,
  Outcome,
  PackState,
  PickupPoint,
  PointsEntry,
  Profile,
  PublicTicket,
  QuestView,
  Rank,
  RedeemResult,
  Session,
  Slide,
  Sphere,
  TeamView,
  VisitStats,
} from './types';

/** Ошибка API с сообщением, которое можно показать человеку. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields?: Record<string, string>,
    /** В какой акции остановился шаг — приходит с 428 «нужно согласие». */
    readonly campaignId?: string,
    /** Три цифры на выбор — приходят с 428 «выбери цифру». */
    readonly picks?: number[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Поле формы не прошло проверку на сервере. */
  fieldError(name: string): string | undefined {
    return this.fields?.[name];
  }
}

export type Audience = 'app' | 'console' | 'admin';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Текст вместо JSON — для загрузки CSV с кодами. */
  text?: string;
  query?: Record<string, string | number | undefined>;
  /** Запрос без токена: публичные эндпоинты. */
  anonymous?: boolean;
}

export class Api {
  private token: string | null = null;

  /**
   * Язык контента: уезжает параметром в каждый запрос.
   *
   * Переводы акций подставляет сервер — он один для приложения, серверных
   * страниц и писем, и три места подстановки означали бы три разных текста у
   * одной акции.
   */
  lang = 'ru';
  private readonly listeners = new Set<(token: string | null) => void>();

  constructor(
    /**
     * Базовый адрес API. Публичный, потому что вход через внешнего
     * поставщика — это переход браузера, а не запрос: ссылку надо собрать
     * руками, и подделать ей нечего.
     */
    public readonly baseUrl: string,
    private readonly storageKey: string,
  ) {
    try {
      this.token = localStorage.getItem(storageKey);
    } catch {
      // Приватный режим: сессия просто не переживёт перезагрузку.
      this.token = null;
    }
  }

  /** Текущий токен. null — не вошли. */
  get session(): string | null {
    return this.token;
  }

  setToken(token: string | null): void {
    this.token = token;
    try {
      if (token) localStorage.setItem(this.storageKey, token);
      else localStorage.removeItem(this.storageKey);
    } catch {
      /* ignore */
    }
    for (const fn of this.listeners) fn(token);
  }

  /** Подписка на смену сессии — приложение редиректит на вход. */
  onSessionChange(fn: (token: string | null) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = new URL(this.baseUrl + path, window.location.origin);
    for (const [k, v] of Object.entries(options.query ?? {})) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
    // Язык контента — в каждый запрос: переводы подставляет сервер.
    if (this.lang !== 'ru' && !url.searchParams.has('lang')) {
      url.searchParams.set('lang', this.lang);
    }

    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.text !== undefined) headers['Content-Type'] = 'text/plain; charset=utf-8';
    if (this.token && !options.anonymous) headers['Authorization'] = `Bearer ${this.token}`;

    let response: Response;
    try {
      response = await fetch(url.toString(), {
        method: options.method ?? 'GET',
        headers,
        body:
          options.text !== undefined
            ? options.text
            : options.body !== undefined
              ? JSON.stringify(options.body)
              : undefined,
      });
    } catch {
      // fetch отклоняется только на сетевой ошибке — отличаем её от ответа
      // сервера, чтобы предложить «повторить», а не «что-то сломалось».
      throw new ApiError(0, 'Нет связи с сервером. Проверь интернет и попробуй снова.');
    }

    if (response.status === 204) return undefined as T;

    const raw = await response.text();
    let parsed: unknown = null;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = null;
      }
    }

    if (!response.ok) {
      const body = parsed as {
        error?: string;
        fields?: Record<string, string>;
        campaign_id?: string;
        picks?: number[];
      } | null;
      if (response.status === 401 && !options.anonymous) this.setToken(null);
      // Пятисотые — это «сервер не смог», а не «ты что-то не так ввёл».
      // «Ошибка 502» человеку не говорит ничего и отправляет искать вину в
      // своих данных: ровно так потерянный сервер выглядит как неверный
      // пароль.
      const fallback =
        response.status >= 500
          ? 'Сервер не отвечает. Это не твои данные — попробуй через минуту.'
          : `Ошибка ${response.status}`;
      throw new ApiError(
        response.status,
        body?.error ?? fallback,
        body?.fields,
        body?.campaign_id,
        body?.picks,
      );
    }
    return parsed as T;
  }

  // ─── публичное ───

  health() {
    return this.request<{ status: string; driver: string; env: string }>('/api/v1/health', { anonymous: true });
  }

  catalog() {
    return this.request<Catalog>('/api/v1/catalog', { anonymous: true });
  }

  /**
   * Лента акций. Пустые поля фильтра не отправляются: лента без параметров —
   * это главный экран, и он не должен зависеть от того, что умеет клиент.
   */
  feed(filter: FeedFilter = {}) {
    return this.request<Feed>('/api/v1/feed', {
      query: {
        region: filter.region,
        subregion: filter.subregion,
        sphere: filter.sphere,
        recipe: filter.recipe,
        sort: filter.sort,
        q: filter.q,
      },
    });
  }

  /** Экран акции. `link` открывает содержимое скрытой карточки. */
  campaign(id: string, link?: string) {
    return this.request<CampaignView>(`/api/v1/campaigns/${id}`, { query: { k: link } });
  }

  participants(id: string, after = 0, limit = 100) {
    return this.request<{ participants: PublicTicket[]; next: number }>(
      `/api/v1/campaigns/${id}/participants`,
      { query: { after, limit }, anonymous: true },
    );
  }

  drawReport(id: string) {
    return this.request<DrawReport>(`/api/v1/campaigns/${id}/draw`, { anonymous: true });
  }

  /** Публичный набор данных для верификатора. */
  verifyBundle(id: string) {
    return this.request<import('@allswipes/fair').VerifyBundle>(`/api/v1/campaigns/${id}/verify`, {
      anonymous: true,
    });
  }

  brands() {
    return this.request<{ brands: Brand[] }>('/api/v1/brands', { anonymous: true });
  }

  brand(slug: string) {
    return this.request<import('./types').BrandPage>(`/api/v1/brands/${slug}`, {
      anonymous: true,
    });
  }

  // ─── вход покупателя ───

  /** Код для входа. kind: 'email' — остальные способы пока отвечают «скоро». */
  requestCode(login: string, kind: 'email' | 'telegram' = 'email') {
    return this.request<OTPRequest>('/api/v1/auth/otp/request', {
      method: 'POST',
      body: { login, kind },
      anonymous: true,
    });
  }

  async verifyOtp(challengeId: string, code: string, region: string) {
    const session = await this.request<Session>('/api/v1/auth/otp/verify', {
      method: 'POST',
      body: { challenge_id: challengeId, code, region },
      anonymous: true,
    });
    this.setToken(session.token);
    return session;
  }

  // ─── приложение покупателя ───

  me() {
    return this.request<Profile>('/api/v1/me');
  }

  /** Смена региона. 403 — почта не подтверждена. */
  setRegion(region: string) {
    return this.request<{ region: string; user: import('./types').User }>('/api/v1/me/region', {
      method: 'PUT',
      body: { region },
    });
  }

  /** Область внутри страны. Пустая строка убирает её. */
  setSubregion(subregion: string) {
    return this.request<{ subregion: string; user: import('./types').User }>(
      '/api/v1/me/subregion',
      { method: 'PUT', body: { subregion } },
    );
  }

  saveContacts(phone: string, telegram: string) {
    return this.request<import('./types').User>('/api/v1/me/contacts', {
      method: 'PUT',
      body: { phone, telegram },
    });
  }

  profileData() {
    return this.request<import('./types').ProfileData>('/api/v1/me/profile');
  }

  saveProfileData(values: Record<string, string>) {
    return this.request<import('./types').ProfileData>('/api/v1/me/profile', {
      method: 'PUT',
      body: { values },
    });
  }

  /** Что акция запрашивает и что из этого уже заполнено. */
  consent(campaignId: string) {
    return this.request<import('./types').ConsentRequest>(
      `/api/v1/me/campaigns/${campaignId}/consent`,
    );
  }

  giveConsent(campaignId: string, values: Record<string, string>) {
    return this.request<import('./types').ConsentRequest>(
      `/api/v1/me/campaigns/${campaignId}/consent`,
      { method: 'POST', body: { values } },
    );
  }

  /**
   * Гашение кода. pick — цифра участника у акций с розыгрышем, link — ключ
   * канала, по ссылке которого человек пришёл.
   */
  redeem(code: string, pick = 0, link?: string) {
    return this.request<RedeemResult>('/api/v1/me/redeem', {
      method: 'POST',
      body: { code, pick, link },
    });
  }

  myEntries() {
    return this.request<{ entries: Entry[] }>('/api/v1/me/entries');
  }

  myWins() {
    return this.request<{ wins: Claim[] }>('/api/v1/me/wins');
  }

  points() {
    return this.request<{ balance: number; ledger: PointsEntry[]; rank: Rank; next_rank?: Rank }>(
      '/api/v1/me/points',
    );
  }

  notifications() {
    return this.request<{ notifications: Notification[]; unread: number }>('/api/v1/me/notifications');
  }

  markNotificationsRead() {
    return this.request<void>('/api/v1/me/notifications/read', { method: 'POST' });
  }

  setReminder(campaignId: string, kind: 'start' | 'draw', on: boolean) {
    return this.request<{ on: boolean }>('/api/v1/me/reminders', {
      method: 'POST',
      body: { campaign_id: campaignId, kind, on },
    });
  }

  claimPrize(claimId: string) {
    return this.request<Claim>(`/api/v1/claims/${claimId}/claim`, { method: 'POST' });
  }

  confirmReceived(claimId: string) {
    return this.request<Claim>(`/api/v1/claims/${claimId}/received`, { method: 'POST' });
  }

  reviewPrize(claimId: string, rating: number, text: string) {
    return this.request<Claim>(`/api/v1/claims/${claimId}/review`, {
      method: 'POST',
      body: { rating, text },
    });
  }

  // ─── механики без кода ───

  quizView(campaignId: string) {
    return this.request<import('./types').QuizView>(`/api/v1/campaigns/${campaignId}/quiz`);
  }

  submitQuiz(campaignId: string, answers: number[], link?: string) {
    return this.request<import('./types').QuizResult>(`/api/v1/me/campaigns/${campaignId}/quiz`, {
      method: 'POST',
      body: { answers, link },
    });
  }

  streakView(campaignId: string) {
    return this.request<import('./types').StreakView>(`/api/v1/campaigns/${campaignId}/streak`);
  }

  checkIn(campaignId: string, link?: string) {
    return this.request<import('./types').StreakResult>(
      `/api/v1/me/campaigns/${campaignId}/checkin`,
      { method: 'POST', body: { link } },
    );
  }

  inviteView(campaignId: string) {
    return this.request<import('./types').InviteView>(
      `/api/v1/me/campaigns/${campaignId}/invite`,
    );
  }

  joinByInvite(code: string) {
    return this.request<import('./types').InviteView>(
      `/api/v1/me/invites/${encodeURIComponent(code)}`,
      { method: 'POST' },
    );
  }

  outcomeProof(outcomeId: string) {
    return this.request<{
      outcome: Outcome;
      weights: import('./types').WeightedPrize[];
      bundle: import('@allswipes/fair').VerifyBundle;
      server_seed?: string;
    }>(`/api/v1/outcomes/${outcomeId}/proof`);
  }

  // ─── кабинет партнёра ───

  /** Цепочка шагов: что пройдено и что дальше. */
  quest(campaignId: string) {
    return this.request<QuestView>(`/api/v1/campaigns/${campaignId}/quest`);
  }

  /** Обмен «данные за ценность»: что просят и что дают. */
  dataDeal(campaignId: string) {
    return this.request<DataDealView>(`/api/v1/campaigns/${campaignId}/deal`);
  }

  /** Принять обмен: передать данные и получить награду. */
  acceptDataDeal(campaignId: string, link?: string) {
    return this.request<RedeemResult>(`/api/v1/me/campaigns/${campaignId}/deal`, {
      method: 'POST',
      body: { link },
    });
  }

  // ─── конкурсы ───

  /** Конкурсы: идущие и завершённые. Черновики сюда не попадают. */
  contests(query: { region?: string } = {}) {
    return this.request<{ contests: Contest[] }>('/api/v1/contests', { query });
  }

  /** Экран конкурса: условия, работы, своя работа и остаток голосов. */
  contest(idOrSlug: string) {
    return this.request<ContestView>(`/api/v1/contests/${idOrSlug}`);
  }

  /** Прислать работу. Одна на человека; повторная переписывает свою же. */
  submitEntry(contestId: string, entry: Partial<ContestEntry>) {
    return this.request<ContestEntry>(`/api/v1/me/contests/${contestId}/entry`, {
      method: 'POST',
      body: entry,
    });
  }

  /** Проголосовать за работу. */
  vote(contestId: string, entryId: string) {
    return this.request<ContestView>(`/api/v1/me/contests/${contestId}/vote`, {
      method: 'POST',
      body: { entry_id: entryId },
    });
  }

  // ─── конкурсы: платформа ───

  adminContests() {
    return this.request<{
      contests: Contest[];
      kinds: Record<string, string>;
      vote_modes: Record<string, string>;
      statuses: Record<string, string>;
    }>('/api/v1/admin/contests');
  }

  adminSaveContest(contest: Partial<Contest>) {
    return contest.id
      ? this.request<Contest>(`/api/v1/admin/contests/${contest.id}`, { method: 'PUT', body: contest })
      : this.request<Contest>('/api/v1/admin/contests', { method: 'POST', body: contest });
  }

  adminContestStage(id: string, status: ContestStatus) {
    return this.request<Contest>(`/api/v1/admin/contests/${id}/stage`, {
      method: 'POST',
      body: { status },
    });
  }

  adminContestEntries(id: string, status?: EntryStatus) {
    return this.request<{ entries: ContestEntry[] }>(`/api/v1/admin/contests/${id}/entries`, {
      query: { status },
    });
  }

  adminModerateEntry(entryId: string, approve: boolean, note?: string) {
    return this.request<{ ok: boolean }>(`/api/v1/admin/entries/${entryId}`, {
      method: 'PUT',
      body: { approve, note },
    });
  }

  /**
   * Подписка на новости и акции.
   *
   * Живёт отдельно от аккаунта: подписаться можно, не заводя его, а участие в
   * акции само по себе согласием на рассылку не является.
   */
  subscribe(email: string, region?: string, source = 'feed') {
    return this.request<{ ok: boolean }>('/api/v1/subscribe', {
      method: 'POST',
      body: { email, region, source },
    });
  }

  /** Условие по ссылке канала: то, ради чего человек нажал на баннер. */
  link(key: string) {
    return this.request<LinkView>(`/api/v1/links/${encodeURIComponent(key)}`);
  }

  /** Каналы акции в кабинете партнёра. */
  campaignLinks(campaignId: string) {
    return this.request<{ links: CampaignLink[]; base: string; app: string }>(
      `/api/v1/console/campaigns/${campaignId}/links`,
    );
  }

  saveCampaignLink(campaignId: string, link: Partial<CampaignLink>) {
    return this.request<CampaignLink>(`/api/v1/console/campaigns/${campaignId}/links`, {
      method: 'POST',
      body: link,
    });
  }

  /** Архив: что платформа уже провела, по годам. Токен не нужен. */
  archive(query: { region?: string; from?: string; to?: string } = {}) {
    // Токен здесь нужен: без входа архив показывает только начало списка.
    return this.request<ArchiveView>('/api/v1/archive', { query });
  }

  /** Убрать акцию из ленты в архив или вернуть обратно. */
  adminArchiveCampaign(id: string, archived: boolean) {
    return this.request<{ archived: boolean }>(`/api/v1/admin/campaigns/${id}/archive`, {
      method: 'POST',
      body: { archived },
    });
  }

  /** Эфиры: расписание розыгрышей платформы. Токен не нужен. */
  broadcast(query: { region?: string; limit?: number } = {}) {
    return this.request<Broadcast>('/api/v1/broadcast', { query, anonymous: true });
  }

  async consoleLogin(email: string, password: string) {
    const session = await this.request<Session>('/api/v1/console/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    });
    this.setToken(session.token);
    return session;
  }

  consoleMe() {
    return this.request<ConsoleMe>('/api/v1/console/me');
  }

  saveBrand(brand: Partial<Brand>) {
    return this.request<Brand>('/api/v1/console/brand', { method: 'PUT', body: brand });
  }

  // ─── платформенная админка ───
  //
  // Отдельный набор ручек и отдельный ключ токена: сессия сотрудника
  // платформы не должна смешиваться с сессией партнёра даже в одном браузере.

  async adminLogin(email: string, password: string) {
    const session = await this.request<AdminSession>('/api/v1/admin/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    });
    this.setToken(session.token);
    return session;
  }

  adminMe() {
    return this.request<AdminMe>('/api/v1/admin/me');
  }

  adminOverview() {
    return this.request<AdminOverview>('/api/v1/admin/overview');
  }

  adminBrands() {
    return this.request<{ brands: Brand[] }>('/api/v1/admin/brands');
  }

  adminSetBrand(
    id: string,
    patch: {
      verified?: boolean;
      blocked?: boolean;
      sphere?: string;
      reason?: string;
      video_upload?: '' | 'on' | 'off';
      demo?: boolean;
    },
  ) {
    return this.request<Brand>(`/api/v1/admin/brands/${id}`, { method: 'PUT', body: patch });
  }

  /** Завести бренд от имени платформы. Публичной регистрации у кабинета нет. */
  /**
   * Заявка компании на проведение акций.
   *
   * Доступа не открывает: решение принимает живой человек. Это «напиши нам»,
   * превращённое в список, который не теряется.
   */
  requestCompanyAccess(draft: CompanyRequestDraft) {
    return this.request<{ ok: boolean; id: string }>('/api/v1/company-requests', {
      method: 'POST',
      body: draft,
      anonymous: true,
    });
  }

  /** Экран оффера: что дают и сколько осталось. */
  offer(campaignId: string) {
    return this.request<OfferView>(`/api/v1/campaigns/${campaignId}/offer`);
  }

  /** Получить предложение. Ссылка приходит в ответе и только взявшему. */
  takeOffer(campaignId: string, link?: string) {
    return this.request<OfferView>(`/api/v1/me/campaigns/${campaignId}/offer`, {
      method: 'POST',
      body: { link },
    });
  }

  /** Точки выдачи. Правятся и после запуска: закрывшийся магазин надо убрать. */
  consoleSavePickup(campaignId: string, pickup: PickupPoint[]) {
    return this.request<Campaign>(`/api/v1/console/campaigns/${campaignId}/pickup`, {
      method: 'PUT',
      body: { pickup },
    });
  }

  /** Призы с упаковки со счётчиками и последние выдачи. */
  consolePack(campaignId: string) {
    return this.request<PackState>(`/api/v1/console/campaigns/${campaignId}/pack`);
  }

  /** Отметить выдачу приза с упаковки. */
  consoleIssuePack(campaignId: string, prizeIndex: number, point: string, note = '') {
    return this.request<PackState>(`/api/v1/console/campaigns/${campaignId}/pack`, {
      method: 'POST',
      body: { prize_index: prizeIndex, point, note },
    });
  }

  /** Состав бренда и живые приглашения. */
  consoleTeam() {
    return this.request<TeamView>('/api/v1/console/team');
  }

  /** Зовёт человека в кабинет. Ссылка возвращается один раз, в этом ответе. */
  consoleInviteMember(email: string, role: BrandRole) {
    return this.request<{ invite: BrandInvite; url: string }>('/api/v1/console/team', {
      method: 'POST',
      body: { email, role },
    });
  }

  consoleRemoveMember(email: string) {
    return this.request<{ ok: boolean }>(
      `/api/v1/console/team/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    );
  }

  consoleRevokeMemberInvite(inviteId: string) {
    return this.request<{ ok: boolean }>(`/api/v1/console/team/invites/${inviteId}`, {
      method: 'DELETE',
    });
  }

  /** Заявки компаний: кто просится проводить акции. */
  adminCompanyRequests(status?: string) {
    return this.request<{ requests: CompanyRequest[] }>('/api/v1/admin/company-requests', {
      query: { status },
    });
  }

  /** Чем кончилась заявка. Отказ хранится наравне с приглашением. */
  adminHandleCompanyRequest(id: string, status: string, brandId = '') {
    return this.request<{ ok: boolean }>(`/api/v1/admin/company-requests/${id}`, {
      method: 'PUT',
      body: { status, brand_id: brandId },
    });
  }

  adminCreateBrand(draft: BrandDraft) {
    return this.request<Brand>('/api/v1/admin/brands', { method: 'POST', body: draft });
  }

  /** Правка данных бренда. Отдельно от пометок платформы: у тех свой метод. */
  adminUpdateBrand(brandId: string, draft: BrandDraft) {
    return this.request<Brand>(`/api/v1/admin/brands/${brandId}`, { method: 'POST', body: draft });
  }

  adminBrandInvites(brandId: string) {
    return this.request<{ invites: BrandInvite[] }>(`/api/v1/admin/brands/${brandId}/invites`);
  }

  /**
   * Выдать партнёру ссылку на заведение кабинета.
   *
   * Ссылка приходит в этом ответе ровно один раз: в базе лежит только её
   * отпечаток. Потеряли — выдаётся новая, старая отзывается.
   */
  adminInviteBrand(brandId: string, email: string, role: 'owner' | 'manager' | 'operator' = 'owner') {
    return this.request<{ invite: BrandInvite; url: string }>(
      `/api/v1/admin/brands/${brandId}/invites`,
      { method: 'POST', body: { email, role } },
    );
  }

  adminRevokeInvite(brandId: string, inviteId: string) {
    return this.request<void>(`/api/v1/admin/brands/${brandId}/invites/${inviteId}`, {
      method: 'DELETE',
    });
  }

  /** Что видит партнёр, открыв пригласительную ссылку. Без токена сессии. */
  invitePreview(token: string) {
    return this.request<InvitePreview>(`/api/v1/console/invites/${encodeURIComponent(token)}`, {
      anonymous: true,
    });
  }

  /**
   * Завести кабинет по приглашению: пароль придумывает сам партнёр.
   *
   * Сессия сохраняется здесь же, как при обычном входе: человек только что
   * доказал право на этот аккаунт, и просить его тут же ввести пароль ещё
   * раз — лишний шаг на пустом месте.
   */
  async acceptInvite(token: string, name: string, password: string) {
    const session = await this.request<Session>(
      `/api/v1/console/invites/${encodeURIComponent(token)}`,
      { method: 'POST', body: { name, password }, anonymous: true },
    );
    this.setToken(session.token);
    return session;
  }

  adminCampaigns(query: { brand?: string; status?: string } = {}) {
    return this.request<{ campaigns: Campaign[]; brands: Brand[] }>('/api/v1/admin/campaigns', {
      query,
    });
  }

  adminSaveDraft(brandId: string, draft: CampaignDraft) {
    return this.request<Campaign>('/api/v1/admin/campaigns', {
      method: 'POST',
      body: { brand_id: brandId, draft },
    });
  }

  adminPublish(id: string) {
    return this.request<Campaign>(`/api/v1/admin/campaigns/${id}/publish`, { method: 'POST' });
  }

  adminCloseCampaign(id: string) {
    return this.request<{ ok: boolean }>(`/api/v1/admin/campaigns/${id}/close`, { method: 'POST' });
  }

  adminReviews(query: { only?: string; brand?: string; limit?: number } = {}) {
    return this.request<{ reviews: ModeratedReview[] }>('/api/v1/admin/reviews', { query });
  }

  adminHideReview(claimId: string, hidden: boolean, reason?: string) {
    return this.request<{ ok: boolean }>(`/api/v1/admin/reviews/${claimId}`, {
      method: 'PUT',
      body: { hidden, reason },
    });
  }

  /**
   * Загрузка файла: фото призового фонда или ролик.
   *
   * multipart, а не JSON с base64: файл на 50 МБ в base64 вырастает на треть и
   * собирается в памяти обеих сторон.
   */
  async uploadMedia(file: File, audience: 'console' | 'admin' | 'me' = 'console') {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${this.baseUrl}/api/v1/${audience}/media`, {
      method: 'POST',
      headers: this.token ? { authorization: `Bearer ${this.token}` } : undefined,
      body: form,
    });
    const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
    if (!res.ok || !body.url) {
      throw new ApiError(res.status, body.error ?? 'Не получилось загрузить файл');
    }
    return body.url;
  }

  // ─── намерения ───

  /** Словарь намерения: атрибуты сферы, стадии, сроки, причины закрытия. */
  intentDict(sphere: string) {
    return this.request<IntentDict>('/api/v1/intents/attrs', {
      query: { sphere },
      anonymous: true,
    });
  }

  /** Ход разговора: сервер возвращает следующий вопрос и всё, что понял. */
  intentChat(said: string, draft: Partial<IntentDraft>) {
    return this.request<ChatTurn>('/api/v1/me/intents/chat', {
      method: 'POST',
      body: { said, draft },
    });
  }

  myIntents() {
    return this.request<{ intents: Intent[] }>('/api/v1/me/intents');
  }

  saveIntent(draft: IntentDraft) {
    return this.request<Intent>('/api/v1/me/intents', { method: 'POST', body: draft });
  }

  intent(id: string) {
    return this.request<IntentView>(`/api/v1/me/intents/${id}`);
  }

  /** Открывает намерение и сразу считает подбор. */
  activateIntent(id: string) {
    return this.request<IntentView>(`/api/v1/me/intents/${id}/activate`, { method: 'POST' });
  }

  /** Закрывает намерение с причиной из закрытого списка. */
  closeIntent(id: string, outcome: string) {
    return this.request<Intent>(`/api/v1/me/intents/${id}/close`, {
      method: 'POST',
      body: { outcome },
    });
  }

  /**
   * Посещаемость: визиты, посетители, страны и источники.
   *
   * Промежуток задаётся двумя датами; без них сервер берёт последние 30
   * суток. Он же возвращает границы обратно — экран показывает то, что
   * посчитано, а не то, что просил.
   */
  adminVisits(range: { from?: string; to?: string } = {}) {
    return this.request<VisitStats>('/api/v1/admin/visits', { query: { ...range } });
  }

  /** Ежедневные копии данных участников. */
  adminBackups() {
    return this.request<{ backups: BackupFile[]; dir: string; keep_days: number }>(
      '/api/v1/admin/backups',
    );
  }

  adminRunBackup() {
    return this.request<{ name: string; made: boolean }>('/api/v1/admin/backups', {
      method: 'POST',
    });
  }

  /**
   * Маячок посещения экрана.
   *
   * Приложение рисуется в браузере, и сервер его переходов не видит. Уходит
   * вид экрана и путь без параметров — ни адреса, ни cookie.
   */
  hit(kind: 'app' | 'console' | 'admin', path: string) {
    return this.request<void>('/api/v1/hit', {
      method: 'POST',
      body: { kind, path: path.split('?')[0] },
      anonymous: true,
    }).catch(() => undefined);
  }

  /** Настройки платформы: переключатели, которые платформа держит за собой. */
  /** Список рассылки файлом — он уезжает в почтовый сервис. */
  async exportSubscribers() {
    const res = await fetch(`${this.baseUrl}/api/v1/admin/subscribers.csv`, {
      headers: this.token ? { authorization: `Bearer ${this.token}` } : undefined,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, body.error ?? 'Не получилось выгрузить');
    }
    return { blob: await res.blob(), name: 'subscribers.csv' };
  }

  adminSettings() {
    return this.request<{ settings: Record<string, string> }>('/api/v1/admin/settings');
  }

  adminSaveSetting(key: string, value: string) {
    return this.request<{ settings: Record<string, string> }>('/api/v1/admin/settings', {
      method: 'PUT',
      body: { key, value },
    });
  }

  adminSpheres() {
    return this.request<{ spheres: Sphere[]; icons: string[] }>('/api/v1/admin/spheres');
  }

  adminSaveSphere(sphere: Partial<Sphere> & { key: string }, isNew: boolean) {
    return isNew
      ? this.request<Sphere>('/api/v1/admin/spheres', { method: 'POST', body: sphere })
      : this.request<Sphere>(`/api/v1/admin/spheres/${sphere.key}`, { method: 'PUT', body: sphere });
  }

  adminDeleteSphere(key: string) {
    return this.request<{ ok: boolean }>(`/api/v1/admin/spheres/${key}`, { method: 'DELETE' });
  }

  adminSetCampaignFlags(
    id: string,
    flags: { demo?: boolean; prizes_verified?: boolean; secret_details?: boolean },
  ) {
    return this.request<Campaign>(`/api/v1/admin/campaigns/${id}`, { method: 'PUT', body: flags });
  }

  adminSlides() {
    return this.request<{ slides: Slide[]; arts: string[] }>('/api/v1/admin/slides');
  }

  adminSaveSlide(slide: Partial<Slide>) {
    return slide.id
      ? this.request<Slide>(`/api/v1/admin/slides/${slide.id}`, { method: 'PUT', body: slide })
      : this.request<Slide>('/api/v1/admin/slides', { method: 'POST', body: slide });
  }

  adminDeleteSlide(id: string) {
    return this.request<{ ok: boolean }>(`/api/v1/admin/slides/${id}`, { method: 'DELETE' });
  }

  adminStaff() {
    return this.request<{ admins: Admin[]; roles: string[] }>('/api/v1/admin/staff');
  }

  adminSaveStaff(draft: {
    id?: string;
    email: string;
    name: string;
    role: string;
    password?: string;
    disabled: boolean;
  }) {
    return draft.id
      ? this.request<Admin>(`/api/v1/admin/staff/${draft.id}`, { method: 'PUT', body: draft })
      : this.request<Admin>('/api/v1/admin/staff', { method: 'POST', body: draft });
  }

  adminAudit(query: { entity?: string; limit?: number } = {}) {
    return this.request<{ entries: AuditEntry[] }>('/api/v1/admin/audit', { query });
  }

  /**
   * Ролик и доказательство призов. Отдельно от черновика: их можно добавить и
   * после запуска — это не условие акции, а то, чем партнёр его подкрепляет.
   */
  saveCampaignProof(
    id: string,
    proof: { video_url?: string; prize_proof_url?: string; prize_proof_note?: string },
  ) {
    return this.request<Campaign>(`/api/v1/console/campaigns/${id}/proof`, {
      method: 'PUT',
      body: proof,
    });
  }

  consoleReviews() {
    return this.request<{ rating: import('./types').Rating; reviews: import('./types').Review[] }>(
      '/api/v1/console/reviews',
    );
  }

  /**
   * Выгрузка участников акции таблицей.
   *
   * Файл скачивается через fetch, а не ссылкой: ручка требует токен, а в
   * адресе его нести нельзя — он утечёт в историю браузера и в журналы.
   */
  async exportParticipants(campaignId: string) {
    const res = await fetch(
      `${this.baseUrl}/api/v1/console/campaigns/${campaignId}/participants.csv`,
      { headers: this.token ? { authorization: `Bearer ${this.token}` } : undefined },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, body.error ?? 'Не получилось выгрузить участников');
    }
    const name =
      /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
      'uchastniki.csv';
    return { blob: await res.blob(), name };
  }

  /** Сводка по переданным данным: счётчики, не значения. */
  collectedData(campaignId: string) {
    return this.request<import('./types').CollectedData>(
      `/api/v1/console/campaigns/${campaignId}/data`,
    );
  }

  consoleCampaigns() {
    return this.request<{ campaigns: Campaign[] }>('/api/v1/console/campaigns');
  }

  consoleCampaign(id: string) {
    return this.request<ConsoleCampaignDetail>(`/api/v1/console/campaigns/${id}`);
  }

  /** Экономика акции. 403 — у бренда не тариф «Про». */
  economics(campaignId: string) {
    return this.request<import('./types').EconomicsView>(
      `/api/v1/console/campaigns/${campaignId}/economics`,
    );
  }

  saveEconomics(campaignId: string, input: import('./types').Economics) {
    return this.request<import('./types').EconomicsView>(
      `/api/v1/console/campaigns/${campaignId}/economics`,
      { method: 'PUT', body: input },
    );
  }

  saveDraft(draft: CampaignDraft) {
    return this.request<Campaign>('/api/v1/console/campaigns', { method: 'POST', body: draft });
  }

  previewRules(draft: CampaignDraft) {
    return this.request<{ rules: string; hash: string }>('/api/v1/console/preview/rules', {
      method: 'POST',
      body: draft,
    });
  }

  /** HTML страницы акции по несохранённому черновику — для iframe в мастере. */
  async previewLanding(draft: CampaignDraft): Promise<string> {
    const response = await fetch(this.baseUrl + '/api/v1/console/preview/landing', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify(draft),
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'Не удалось собрать превью страницы');
    }
    return response.text();
  }

  publish(id: string) {
    return this.request<{ campaign: Campaign; landing_url: string }>(
      `/api/v1/console/campaigns/${id}/publish`,
      { method: 'POST' },
    );
  }

  duplicate(id: string) {
    return this.request<Campaign>(`/api/v1/console/campaigns/${id}/duplicate`, { method: 'POST' });
  }

  closeCampaign(id: string) {
    return this.request<{ status: string }>(`/api/v1/console/campaigns/${id}/close`, { method: 'POST' });
  }

  runDraw(id: string, externalSource: string, externalValue: string) {
    return this.request<DrawReport>(`/api/v1/console/campaigns/${id}/draw`, {
      method: 'POST',
      body: { external_source: externalSource, external_value: externalValue },
    });
  }

  /** Загрузка партии кодов: CSV или по одному коду на строку. */
  importCodes(id: string, codes: string, label: string) {
    return this.request<CodeBatch>(`/api/v1/console/campaigns/${id}/codes`, {
      method: 'POST',
      text: codes,
      query: { label },
    });
  }

  consoleClaims(statuses?: ClaimStatus[]) {
    return this.request<{ claims: Claim[] }>('/api/v1/console/claims', {
      query: { status: statuses?.join(',') },
    });
  }

  markClaimReady(claimId: string, pickupPoint: string) {
    return this.request<Claim>(`/api/v1/console/claims/${claimId}/ready`, {
      method: 'POST',
      body: { pickup_point: pickupPoint },
    });
  }

  audit(entity?: string, entityId?: string) {
    return this.request<{ entries: AuditEntry[] }>('/api/v1/console/audit', {
      query: { entity, entity_id: entityId },
    });
  }
}
